<template>
  <q-card style="max-width: 100%; width: 500px">
    <q-form
      ref="formRef"
      @submit.prevent="saveAndCreateNew"
      autocomplete="off"
      greedy
    >
      <q-card-section class="row items-center text-h6">
        <!-- {{ formModal.state.mode === 'create' ? 'Skapa' : 'Redigera' }} -->
        Störningshantering</q-card-section
      >

      <q-separator />

      <q-card-section class="q-pa-none">
        <DeviationEventInput
          :event-uuid="model.eventUuid"
          v-if="showEventInput"
          v-model="model"
        />
        <div class="row">
          <div class="col-5 q-pa-md">
            <q-input
              outlined
              square
              label="Störningsplats"
              :model-value="model.disruptionPlace"
              @update:model-value="
                (v) => (model.disruptionPlace = v.toUpperCase())
              "
              :rules="[requiredRule]"
            />
          </div>
          <div class="col-7 q-pa-md">
            <AppFieldSelect
              label="Aktivitet"
              v-model="model.activity"
              :loading="loadingStateDisruptionReasons.fetchAll"
              :options="deviationDisruptionReasonsParsed"
              :rules="[requiredRule]"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-6 q-pa-md">
            <AppFieldDate
              label="Startdatum"
              v-model="model.departureDate"
              :rules="[validDateRule]"
            />
          </div>
          <div class="col-6 q-pa-md">
            <q-input
              outlined
              square
              type="time"
              v-model="model.departure"
              label="Starttid"
              :rules="[requiredRule]"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-6 q-pa-md">
            <AppFieldDate
              label="Slutdatum"
              v-model="arrivalDate"
              :rules="[validDateRule]"
            />
          </div>
          <div class="col-6 q-pa-md">
            <q-input
              outlined
              square
              type="time"
              v-model="model.arrival"
              label="Sluttid"
              :rules="[requiredRule]"
            />
          </div>
        </div>

        <div class="row">
          <div class="col">
            <DeviationDescriptionInput
              v-model="model.description"
              label="Beskrivning"
            />
          </div>
        </div>
      </q-card-section>

      <q-separator />

      <q-card-actions align="right">
        <q-btn
          flat
          label="Avbryt"
          @click="formModal.closeModal"
          :disable="loading"
        />
        <q-btn
          label="Spara & skapa ny"
          outline
          color="primary"
          type="submit"
          :loading="loading"
        />
        <q-btn
          label="Spara & stäng"
          color="primary"
          :loading="loading"
          @click="onSubmit"
        />
      </q-card-actions>
    </q-form>
  </q-card>
</template>

<script lang="ts">
import { UseModal } from '@/composable/useModal'
import { QForm } from 'quasar'
import { computed, defineComponent, inject, onBeforeMount, ref } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import DeviationDescriptionInput from '@/components/deviation/DeviationDescriptionInput.vue'
import DeviationEventInput from '@/components/deviation/DeviationEventInput.vue'
import AppFieldDate from '@/components/AppFieldDate.vue'
import {
  validDateRule,
  required as requiredRule,
  requiredNotNull,
} from '@/common/formValidationRules'
import { addHours, format } from 'date-fns'
import { DeviationDisruption } from '@/types/deviation-disruption'
import { useDeviationDisruption } from '@/composable/useDeviationDisruption'
import AppFieldSelect from '@/components/AppFieldSelect.vue'
import { useDeviationDisruptionReason } from '@/composable/useDeviationDisruptionReason'

export default defineComponent({
  name: 'DeviationDisruptionFormModal',

  components: {
    DeviationDescriptionInput,
    DeviationEventInput,
    AppFieldDate,
    AppFieldSelect,
  },

  props: {
    eventUuid: {
      type: String,
    },
    showEventInput: Boolean,
  },

  setup(props) {
    const {
      add: addDeviationDisruption,
      update: updateDeviationDisruption,
      loading: loadingStateDeviationDisruption,
    } = useDeviationDisruption()

    const {
      data: deviationDisruptionReasons,
      fetchAll: fetchAllDisruptionReasons,
      loading: loadingStateDisruptionReasons,
    } = useDeviationDisruptionReason()

    fetchAllDisruptionReasons()

    const formModal = inject<UseModal<DeviationDisruption>>(
      'deviation-disruption-form-modal'
    )
    const formRef = ref<QForm | null>(null)
    // const $q = useQuasar()

    const model = ref<Partial<DeviationDisruption>>({
      uuid: uuidv4(),
      disruptionPlace: '',
      departureDate: format(new Date(), 'yyyy-MM-dd'),
      departure: format(new Date(), 'HH:mm'),
      arrival: format(addHours(new Date(), 1), 'HH:mm'),
      description: '',
      activity: '',
      eventUuid: props.eventUuid,
      eventName: null,
      deviationDisruptionActivityUuid: '',
      eventId: null,
      eventIsOpen: null,
      eventDate: null,
    })

    // If update modal set event to model
    onBeforeMount(() => {
      if (formModal?.state.data && formModal?.state.mode === 'update') {
        model.value.eventName = formModal.state.data.eventName
        model.value.uuid = formModal.state.data.uuid
        model.value.departureDate = format(
          new Date(formModal.state.data.departureDate),
          'yyyy-MM-dd'
        )
        model.value.arrival = format(
          new Date(formModal.state.data.arrival),
          'HH:mm'
        )
        model.value.departure = format(
          new Date(formModal.state.data.departure),
          'HH:mm'
        )
        model.value.description = formModal.state.data.description
        model.value.eventUuid = formModal.state.data.eventUuid
        model.value.disruptionPlace = formModal.state.data.disruptionPlace
        model.value.activity = formModal.state.data.activity

        model.value.eventId = formModal.state.data.eventId
        model.value.eventIsOpen = formModal.state.data.eventIsOpen
        model.value.eventDate = formModal.state.data.eventDate
      }
    })

    const arrivalDate = ref(format(new Date(), 'yyyy-MM-dd'))

    const onSubmit = async () => {
      const isValid = await formRef.value?.validate()
      if (!isValid || !deviationDisruptionReasons.value.length)
        return Promise.reject()

      const reason = deviationDisruptionReasons.value.find(
        (x) => x.name === model.value.activity
      )

      if (!reason) return Promise.reject()

      model.value.deviationDisruptionActivityUuid = reason.uuid

      const payload = {
        ...model.value,
        departure: new Date(
          `${model.value.departureDate} ${model.value.departure}`
        ),
        arrival: new Date(`${arrivalDate.value} ${model.value.arrival}`),
      }

      if (formModal?.state.mode === 'create') {
        await addDeviationDisruption(payload)
        formModal?.closeModal()
        // $q.notify({
        //   message: 'Avvikelsen är skapad',
        //   icon: 'mdi-checkbox-marked-circle-outline',
        //   timeout: 3000,
        //   color: 'primary',
        //   textColor: 'white',
        //   position: 'center',
        //   actions: [{ label: 'Ok', color: 'white' }],
        // })
      }

      if (formModal?.state.mode === 'update') {
        await updateDeviationDisruption(payload)
        formModal?.closeModal()
      }
    }

    const saveAndCreateNew = async () => {
      await onSubmit().then(() => {
        window.setTimeout(() => {
          if (formModal) {
            formModal.openModal({ mode: 'create' })
          }
        }, 100)
      })
    }

    const loading = computed(() => {
      return (
        loadingStateDeviationDisruption.value.create ||
        loadingStateDeviationDisruption.value.update
      )
    })

    const deviationDisruptionReasonsParsed = computed(() => {
      return deviationDisruptionReasons.value.map((x) => x.name)
    })

    return {
      loading,
      model,
      formModal,
      formRef,
      onSubmit,
      requiredRule,
      validDateRule,
      requiredNotNull,
      deviationDisruptionReasons,
      loadingStateDisruptionReasons,
      deviationDisruptionReasonsParsed,
      arrivalDate,
      saveAndCreateNew,
    }
  },
})
</script>
